import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row, Skeleton, Space, Tooltip, Typography, message } from 'antd';
import {
  DownOutlined, FileTextOutlined, FilterOutlined, ReloadOutlined, SearchOutlined, UpOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ListView from './ListView.jsx';
import SearchFieldControl from './searchBuilder/SearchFieldControl.jsx';
import BooleanQueryField from './searchBuilder/BooleanQueryField.jsx';
import { fieldHasValue, ownsLabelFor } from './searchBuilder/fieldTypeRegistry.jsx';
import {
  carryExpressionToTerms,
  carryTermsToExpression,
  isExcludeKeywordField,
  isKeywordField,
  isPrimaryKeywordField,
  keywordGroupId,
  normalizeKeywordValue,
  showsMandatoryStars,
} from './searchBuilder/keywordCriteria';
import { BOOLEAN_PARSEABLE_API_KEYS, isBooleanParsable, validateBooleanQuery } from './searchBuilder/booleanQueryUtils';
import { buildBooleanRequest } from './searchBuilder/buildBooleanRequest';
import { buildTraditionalParams } from './searchBuilder/buildTraditionalParams';
import { buildFilterConditions } from './searchBuilder/buildFilterConditions';
import { resolvePresetRange, toEndOfDayString, toStartOfDayString } from './searchBuilder/dateRangePresets';
import { safeNavTarget } from '../utils/afterSubmitNav';
import { getCustomForms } from '../services/adminApi';
import { searchModuleData } from '../services/moduleDataApi';
import { searchBoolean } from '../services/booleanSearchApi';
import { makeSearchFetcher } from '../services/candidateSearchAdapter';
import { mapListResponse } from '../services/responseMapping/mapListResponse';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import {
  buildSourceModuleMap,
  buildSourceTabs,
  getAvailableSourceActions,
  getSearchSources,
  getSourceActionInstanceKey,
  normalizeSourceKey,
  resolveSearchSource,
  runSourceAction,
  sourceActionAppliesToRecord,
} from '../services/sourceProviderActions';
import { resolveDiceProfileId } from '../services/diceCandidateMapper';
import '../styles/SearchBuilderV1.css';

const { Text } = Typography;

// The field key that turns the source picker on. Naming an existing contract
// rather than adding one: runSearch already reads values under exactly this key.
const SOURCE_FIELD = 'selectedSource';

// Picking a source is not itself a search criterion — searching on it alone
// would return the entire collection.
const SOURCE_ONLY_FIELDS = new Set([SOURCE_FIELD, 'source', 'sourceType', 'scope']);

const EMPTY_RESULTS = {
  searched: false, rows: [], total: 0, fields: [], actions: [], actionRules: [], loading: false,
};

function extractGroups(schema) {
  if (Array.isArray(schema)) return schema;
  if (Array.isArray(schema?.groups)) return schema.groups;
  if (Array.isArray(schema?.data?.groups)) return schema.data.groups;
  return [];
}

function truthy(value) {
  return value === true || value === 1 || value === '1';
}

// A range's bounds, resolved exactly as RangeSliderField resolves them so the
// two cannot drift apart.
function rangeBounds(field) {
  return [Number(field.min ?? 0), Number(field.max ?? 30)];
}

// The one place a field's starting value is seeded, wired into every Form.Item
// below via initialValue.
//
// Locked checkbox/radio options (admin-configured — off by default, see
// FormGroupsSection's Options/Values editor) must be selected from the very
// first render, not just visually via the registry renderer's own defensive
// merge — otherwise submitting without ever touching the control would omit
// it from the request entirely. Radio only allows one selection, so its
// locked default is a single value; checkbox's is every locked option.
//
// Two admin-configured defaults land here, and both are REAL values: seeded at
// the Form level, they are submitted even when the user never touches the
// control. That is the point — and it is also why each is opt-in per field
// rather than automatic, so no other module's search form inherits a filter it
// never asked for.
//
//   defaultRange: "full"   a range starts at its full span. The slider already
//                          draws its handles at the bounds, so empty From/To
//                          boxes made the two views of one tuple disagree.
//   defaultPreset: <key>   a date range starts on that preset, resolved through
//                          the same helpers DateRangePresetField uses so
//                          nothing downstream meets a new value shape.
//
// Both narrow every search on the form that configures them — see the callouts
// in docs/common-keyword-search-scenarios.md.
function defaultInitialValue(field) {
  if (field.type === 'range') {
    return String(field.defaultRange ?? '').trim() === 'full' ? rangeBounds(field) : undefined;
  }

  if (field.type === 'daterangePreset') {
    const preset = String(field.defaultPreset ?? '').trim();
    if (!preset) return undefined;
    const [from, to] = resolvePresetRange(preset);
    return { preset, from: toStartOfDayString(from), to: toEndOfDayString(to) };
  }

  const options = Array.isArray(field.options) ? field.options : [];
  const locked = options.filter((option) => option.locked).map((option) => option.value);
  if (!locked.length) return undefined;
  return field.type === 'radio' ? locked[0] : locked;
}

// Whether a field is still sitting on the default it was seeded with. Such a
// value constrains nothing the user chose, so it must not be what enables the
// Search button — otherwise a form carrying either default would have Search
// permanently lit with no criteria entered. Both are still SENT; this is only
// about "has the user narrowed anything".
function isUntouchedDefault(field, value) {
  if (field?.type === 'range') {
    if (String(field.defaultRange ?? '').trim() !== 'full') return false;
    if (!Array.isArray(value) || value.length !== 2) return false;
    const [min, max] = rangeBounds(field);
    return Number(value[0]) === min && Number(value[1]) === max;
  }

  if (field?.type === 'daterangePreset') {
    const preset = String(field.defaultPreset ?? '').trim();
    return Boolean(preset) && value?.preset === preset;
  }

  return false;
}

// Dice rows do not always carry _id, and ListView needs a stable key per row or
// selection attaches to the wrong record after a re-sort or page change.
function resolveRowKey(record, index) {
  return String(
    record?._id
    ?? record?.id
    ?? record?.applicantRefrenceId
    ?? record?.applicantReferanceId
    ?? record?.diceId
    ?? record?.profileId
    ?? index,
  );
}

// A composite value (the keywords control's { terms, expression }) is always a
// truthy object, so a type that declares hasValue answers for itself and the
// generic checks below are the fallback for every other type.
function fieldValueFilled(field, value, context) {
  const declared = field ? fieldHasValue(field, value, context) : undefined;
  if (declared !== undefined) return declared;
  if (value === undefined || value === null || value === '') return false;
  // A value still sitting on its seeded default is not something the user asked
  // for — see isUntouchedDefault.
  if (isUntouchedDefault(field, value)) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function hasCriteria(values, fields = [], context = {}) {
  const byKey = new Map(fields.map((field) => [field.field, field]));
  return Object.entries(values ?? {}).some(([key, value]) => {
    if (SOURCE_ONLY_FIELDS.has(key)) return false;
    const field = byKey.get(key);
    return fieldValueFilled(field, value, {
      ...context,
      isPrimary: isPrimaryKeywordField(field),
    });
  });
}

export default function SearchBuilderV1({
  moduleName,
  action,
  formType = 'search',
  title,
  migrationJobId,
  sourceModules,
  searchActionContext,
  onBack,
}) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const [searchMode, setSearchMode] = useState('traditional');
  const [selectedSource, setSelectedSource] = useState('internal');
  const [fieldErrors, setFieldErrors] = useState({});
  const [tabs, setTabs] = useState([]);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [lastValues, setLastValues] = useState(null);
  const [sourceActionState, setSourceActionState] = useState({});
  const [formValues, setFormValues] = useState({});
  // Unified path only: the submitted criteria, and a nonce that remounts
  // ListView so pressing Search again re-runs even when nothing changed.
  const [criteria, setCriteria] = useState(null);
  const [searchNonce, setSearchNonce] = useState(0);
  // The source selection as it was when Search was pressed. Deliberately NOT
  // read live from formValues — see searchFetcher.
  const [submittedSources, setSubmittedSources] = useState(null);
  const [activeView, setActiveView] = useState('list');
  // Folds the filters away so the results table gets the viewport. Manual
  // only — nothing collapses or expands on its own except the one
  // correctness case in handleSearch below.
  const [formCollapsed, setFormCollapsed] = useState(false);
  const formBodyId = useId();
  // Armed by a search, disarmed by the first response that follows. A plain
  // "collapse whenever results change" would also fire on paging and tab
  // switches, re-collapsing a form the user had deliberately reopened.
  const collapseOnResultsRef = useRef(false);

  // Called with each response, from both dispatch paths. Collapses only when
  // there is something to look at: a search that found nothing leaves the form
  // open, because widening it is the next thing the user needs to do.
  const settleCollapseAfterSearch = useCallback((hasRows) => {
    if (!collapseOnResultsRef.current) return;
    // Disarmed either way, so a later tab switch cannot collapse it.
    collapseOnResultsRef.current = false;
    if (hasRows) setFormCollapsed(true);
  }, []);

  // Every state write happens in a promise callback, and a config that resolves
  // after the props changed (or after unmount) is discarded rather than
  // overwriting the newer one.
  useEffect(() => {
    if (!moduleName) return undefined;
    let cancelled = false;

    getCustomForms(moduleName, formType, action)
      .then((data) => {
        if (cancelled) return;
        setSchema(Array.isArray(data) ? data[0] ?? null : data ?? null);
        setLoadError('');
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error?.message || 'Failed to load the search form configuration.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [moduleName, formType, action, reloadToken]);

  const retry = () => { setLoading(true); setReloadToken((token) => token + 1); };

  // Sorted WITHIN each group, not globally after flattening. Field order is
  // numbered per group from 0 (both the seeders and the admin editor's
  // buildFieldPayload write it that way), so a global sort would interleave
  // group B's field 0 with group A's — the group labels would still render but
  // the fields under them would not be the group's own.
  const fields = useMemo(() => (
    extractGroups(schema)
      .filter((group) => !truthy(group.hideGroup))
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
      .flatMap((group) => (group.fields ?? [])
        .filter((field) => field?.field && field.show !== 0 && !truthy(field.hidden))
        .slice()
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)))
  ), [schema]);

  // Built once and shared by BOTH request paths, so a source tab cannot ask one
  // endpoint for its own module and the other for the form's. Config (the
  // option's `module`) beats the `sourceModules` prop; a source with neither
  // falls back to moduleName, which is every source on every other form.
  const sourceModuleMap = useMemo(
    () => buildSourceModuleMap(fields, sourceModules),
    [fields, sourceModules],
  );

  // Sources are OPT-IN, and this is the switch. Candidates configures a
  // selectedSource field and gets Internal/Dice tabs; a Jobs or Clients search
  // configures none and must not be handed that vocabulary — no selectedSource
  // or scope on the wire, no sourceType in a boolean body, and no phantom
  // "Internal" tab beside the module's own. Everything a plain form needs is
  // then just its module name.
  const hasSourceField = useMemo(
    () => fields.some((field) => field.field === SOURCE_FIELD),
    [fields],
  );

  // Boolean is a property of the common Keywords section, not of individual
  // fields. Exactly one field can own it — the one the admin marked
  // isPrimaryKeyword — and it draws the toggle itself, inside its own block.
  const primaryKeywordField = useMemo(
    () => fields.find((field) => isPrimaryKeywordField(field)) ?? null,
    [fields],
  );

  // A form whose config never opted in shows no toggle at all, so every
  // existing search form behaves exactly as it did before. Stricter than it
  // used to be: without a primary keywords field there is nowhere for the
  // toggle to live and nothing it could switch, so the mode stays off.
  const booleanEnabled = Boolean(schema?.booleanSearchEnabled)
    && Boolean(schema?.booleanSearchEndpoint)
    && Boolean(primaryKeywordField);
  const isBooleanMode = booleanEnabled && searchMode === 'boolean';
  const booleanFields = useMemo(
    () => (primaryKeywordField ? [primaryKeywordField] : []),
    [primaryKeywordField],
  );

  // Surfaced to whoever builds the form rather than to the person searching:
  // a field left over from when Skills/Job Title/Designation could each be an
  // expression box still renders as a normal field, it just says so once.
  useEffect(() => {
    fields
      .filter((field) => field.booleanCapable && !isBooleanParsable(field.booleanApiKey))
      .forEach((field) => {
        console.warn(
          `[SearchBuilderV1] "${field.field}" is marked booleanCapable but its booleanApiKey `
          + `(${JSON.stringify(field.booleanApiKey)}) is not one of ${BOOLEAN_PARSEABLE_API_KEYS.join(', ')} — `
          + 'Boolean now belongs to the common Keywords field only, so this renders as a normal '
          + 'field and its value is matched literally. Clear the flag in Admin → Form Groups.',
        );
      });
  }, [fields]);

  // A field can be configured to disappear in Boolean mode — Exclude Keywords
  // does, because NOT already expresses exclusion inside the expression. It is
  // filtered out of BOTH the render loop and the request builders, so a value
  // it still holds is never submitted from a control the user cannot see.
  const visibleFields = useMemo(
    () => fields.filter((field) => !(isBooleanMode && truthy(field.hideInBooleanMode))),
    [fields, isBooleanMode],
  );

  // The exclude companion of a keyword field: same group, role "exclude". This
  // is where a NOT term goes when an expression is read back into chips.
  const findExcludeCompanion = useCallback((field) => fields.find((candidate) => (
    isKeywordField(candidate)
    && isExcludeKeywordField(candidate)
    && keywordGroupId(candidate) === keywordGroupId(field)
  )), [fields]);

  const handleModeChange = (checked) => {
    // Nothing is ever cleared — the control holds the chips and the expression
    // side by side, so flipping the toggle shows the other half rather than
    // discarding this one. On top of that the values are CARRIED across, so the
    // user is not retyping what they just entered:
    //
    //   on   chips become a comma list, and nothing more. No AND is invented
    //        from a star, no OR from a set.
    //   off  the expression is read back — AND to stars, OR to plain chips, NOT
    //        to the Exclude box — but only when it can be chips at all.
    //
    // Which fields take part comes from config (isPrimaryKeyword = "owns an
    // expression box"), never from a field name.
    const next = {};

    fields.filter(isPrimaryKeywordField).forEach((field) => {
      const current = form.getFieldValue(field.field);

      if (checked) {
        next[field.field] = carryTermsToExpression(current);
        return;
      }

      const { model, exclude, converted } = carryExpressionToTerms(current, {
        starsEnabled: showsMandatoryStars(field),
      });
      // A real query with no chip representation — nested groups, two OR sets —
      // is left exactly as it is rather than flattened into a different search.
      if (!converted) return;

      if (exclude?.length) {
        const companion = findExcludeCompanion(field);
        // Nowhere to put the exclusions. Refuse the whole conversion rather
        // than drop them, which would silently widen the search.
        if (!companion) return;
        next[companion.field] = {
          ...normalizeKeywordValue(form.getFieldValue(companion.field)),
          terms: exclude.map((value) => ({ value, mandatory: false })),
          mandatoryAll: false,
        };
      }

      next[field.field] = model;
    });

    if (Object.keys(next).length) {
      form.setFieldsValue(next);
      // onValuesChange does not fire for a programmatic set, and the
      // Search-enable gate reads formValues.
      setFormValues((values) => ({ ...values, ...next }));
    }

    setFieldErrors({});
    setCriteria(null);
    setSearchMode(checked ? 'boolean' : 'traditional');
  };

  const runSearch = useCallback(async (values, sourceOverride, paginationOverride = {}) => {
    const pageSize = Number(paginationOverride.pageSize) > 0
      ? Number(paginationOverride.pageSize) : pagination.pageSize;
    const requested = Number(paginationOverride.current);
    const current = Number.isFinite(requested) && requested > 0 ? Math.floor(requested) : 1;

    const checkedSources = getSearchSources(values?.[SOURCE_FIELD]);
    // '' when the form has no source picker: resolveSearchSource always lands on
    // something ('internal' by default), which is the right answer only when a
    // source was actually asked for.
    const activeSource = !hasSourceField ? '' : (sourceOverride
      ? resolveSearchSource(sourceOverride, selectedSource)
      : (checkedSources.includes('internal')
        ? 'internal'
        : resolveSearchSource(values?.[SOURCE_FIELD], selectedSource)));

    // A source tab can be served by its OWN module key, so that tab gets its own
    // columns and row actions — the backend keeps the requested key for every
    // config lookup and falls back to the base module for the data (see
    // configModule in moduleDataController.go). No mapping for a source means
    // the form's own moduleName, exactly as before, so every other form and
    // project is unaffected.
    const activeModule = sourceModuleMap[normalizeSourceKey(activeSource)] || moduleName;

    setLastValues(values);
    setPagination({ current, pageSize });
    setFieldErrors({});
    setResults((prev) => ({ ...prev, loading: true, searched: true }));

    try {
      let result;
      if (isBooleanMode) {
        const body = buildBooleanRequest(values, visibleFields, {
          booleanMode: true,
          // Left blank for a form with no source picker, so buildBooleanRequest
          // sends no sourceType at all rather than assuming Candidates.
          defaultSourceType: hasSourceField
            ? (schema?.booleanDefaultSourceType || activeSource || 'internal')
            : '',
          // Same module key the traditional endpoint gets as ?module=, so the
          // backend can resolve fields/actions/rules from the same config
          // whichever mode the search ran in.
          module: activeModule,
        });
        result = await searchBoolean(schema.booleanSearchEndpoint, body, {
          limit: pageSize,
          offset: (current - 1) * pageSize,
        });
      } else {
        // selectedSource is the full ticked list, scope is the one being viewed.
        // That is the contract the API was built for (moduleDataController.go):
        // both enabled -> internal records + a Dice count, in ONE call, with a
        // tab per source. Sending only the active source made the server return
        // a single tab and forced the client to invent the rest of the strip.
        result = await searchModuleData(
          activeModule,
          {
            ...buildTraditionalParams(values, visibleFields),
            ...(activeSource
              ? { [SOURCE_FIELD]: (checkedSources.length ? checkedSources : [activeSource]).join(',') }
              : {}),
          },
          { limit: pageSize, offset: (current - 1) * pageSize },
          { scope: activeSource, filters: buildFilterConditions(values, visibleFields) },
        );
      }

      // With several sources ticked the server's `total` is the COMBINED figure
      // (internal + dice), which makes pagination read "1-10 of 281460" on a tab
      // holding 9 records. The per-source count is the honest one for the tab
      // being viewed; fall back to the total when it is absent.
      const sourceCount = Number(result.counts?.[activeSource]);
      const pagedTotal = Number.isFinite(sourceCount) ? sourceCount : result.total;

      setSelectedSource(activeSource);
      settleCollapseAfterSearch(Number(pagedTotal) > 0 || (result.rows?.length ?? 0) > 0);
      setResults({
        searched: true,
        rows: result.rows,
        total: pagedTotal,
        fields: result.fields ?? [],
        actions: result.actions ?? [],
        actionRules: result.actionRules ?? [],
        loading: false,
      });
      // Only a source form owns its tab strip. Everything else — jobs' My Jobs /
      // All Jobs, a field-based tab set — is the backend's to report, and
      // buildSourceTabs would prepend an Internal tab that module never had.
      if (!hasSourceField) {
        setTabs(result.tabs ?? []);
        return;
      }
      setTabs((currentTabs) => buildSourceTabs({
        // The ticked options are the single source of truth for tab ORDER, in
        // both modes. The server's own list can't be: boolean search runs its
        // sources through sort.Strings (models/boolean_search.go), so it always
        // comes back alphabetical — "dice" before "internal" — which put Dice
        // first the moment the Boolean toggle went on. result.sources is only a
        // fallback for when nothing is ticked.
        sources: checkedSources.length ? checkedSources : result.sources,
        activeSource,
        activeCount: pagedTotal,
        responseCounts: result.counts,
        currentTabs,
        backendTabs: result.tabs,
      }));
    } catch (error) {
      // Point a rejected expression at the field that carries it when the
      // server names one; otherwise fall back to a form-level message.
      const details = error?.details ? String(error.details) : '';
      const blamed = error?.code === 'INVALID_BOOLEAN_QUERY'
        ? booleanFields.find((field) => details.toLowerCase().includes(String(field.field).toLowerCase()))
        : null;
      if (blamed) setFieldErrors({ [blamed.field]: details });
      else message.error(details || error?.message || 'Search failed');

      setResults({ ...EMPTY_RESULTS, searched: true });
    }
  }, [visibleFields, booleanFields, hasSourceField, isBooleanMode, moduleName, pagination.pageSize, schema, selectedSource, settleCollapseAfterSearch, sourceModuleMap]);

  // Which endpoint answers this form, defined in Admin (Form Groups → Form
  // Identity → Search Endpoint). When set, ListView fetches from it for BOTH
  // modes and owns tabs, paging, sort and selection. Blank — the default for
  // every form — keeps the common /module-data-list path unchanged.
  //
  // Deliberately NOT booleanSearchEndpoint: that means "where boolean mode
  // POSTs", so reusing it would silently reroute traditional search on any form
  // that merely enabled the Boolean toggle.
  const unifiedEndpoint = String(schema?.searchEndpoint ?? '').trim();

  // One vocabulary on the wire for both modes: booleanApiKey-mapped params,
  // plus fieldFilters for anything the admin added that has no mapping — which
  // is what keeps custom search fields filtering once the flat query-param
  // path (buildTraditionalParams) is no longer used.
  const buildCriteria = useCallback((values) => {
    const conditions = buildFilterConditions(values, visibleFields);
    return {
      ...buildBooleanRequest(values, visibleFields, {
        booleanMode: isBooleanMode,
        // Blank for a form with no source picker, exactly as runSearch does, so
        // buildBooleanRequest sends no sourceType rather than assuming
        // Candidates. See hasSourceField.
        defaultSourceType: hasSourceField
          ? (schema?.booleanDefaultSourceType || 'internal')
          : '',
      }),
      ...(conditions.length ? { fieldFilters: conditions } : {}),
    };
  }, [visibleFields, hasSourceField, isBooleanMode, schema]);

  // ListView owns the fetching on this path and re-runs it whenever `fetchData`
  // changes identity, so EVERY dependency here is a potential network call.
  //
  // `formValues` used to be one of them, for the sake of reading
  // `selectedSource` off it — and `formValues` is rewritten on every keystroke.
  // The result was a search firing per character typed, long before anyone
  // pressed Search. `submittedSources` is that same selection captured at
  // submit time instead, so nothing here changes until a search is dispatched.
  const searchFetcher = useMemo(() => {
    if (!unifiedEndpoint || !criteria) return null;
    return makeSearchFetcher({
      endpoint: unifiedEndpoint,
      criteria,
      searchMode: isBooleanMode ? 'boolean' : 'traditional',
      sources: submittedSources ?? [],
      // The map, not the raw prop: the adapter's own lookup is right, it was
      // simply never given the modules the config declares.
      sourceModules: sourceModuleMap,
    });
  }, [unifiedEndpoint, criteria, isBooleanMode, submittedSources, sourceModuleMap]);

  // An action that declares BOTH apiPath and routeTemplate means "call the API,
  // then go somewhere" — e.g. Migrate To Internal, which migrates the candidate
  // and then opens its detail view so the user can tag it to a job or move it
  // to a stage. Neither built-in branch does both: actionType "api" calls and
  // stays put, "navigate" goes without calling. Attaching onClick short-circuits
  // ListView's own dispatch (it returns as soon as onClick exists), so this needs
  // no change to the shared component.
  const withPostActionRoute = useCallback((action) => {
    if (!action?.apiPath || !action?.routeTemplate) return action;

    return {
      ...action,
      onClick: async ({ selectedRecords }) => {
        const record = selectedRecords?.[0] ?? {};
        // A Dice row carries no `diceId` — its `_id` IS the provider's profile
        // id — so a template like /dicemigrate/:diceId resolved to an empty
        // segment and called /dicemigrate/. resolveDiceProfileId already knows
        // every spelling the provider uses, including that fallback.
        const diceId = resolveDiceProfileId(record);
        const context = diceId ? { ...record, diceId } : record;
        const apiPath = safeNavTarget(action.apiPath, context._id ?? context.id, context);
        if (!apiPath) {
          message.error(`${action.label}: the configured API path is not a valid in-app path.`);
          return;
        }

        try {
          const method = String(action.apiMethod || 'POST').toUpperCase();
          const response = await fetchJsonWithAuth(AUTH_URL, apiPath, {
            method,
            body: ['GET', 'DELETE'].includes(method)
              ? undefined
              : JSON.stringify({ ids: [record._id ?? record.id], records: [record] }),
          });
          message.success(`${action.label} completed`);

          // The destination may need an id the API only just created, so the
          // response wins over the row it was launched from.
          const routeContext = { ...context, ...(response?.data ?? response ?? {}) };
          const target = safeNavTarget(action.routeTemplate, routeContext._id ?? routeContext.id, routeContext);
          if (target) navigate(target);
        } catch (error) {
          message.error(error?.message || `${action.label} failed`);
        }
      },
    };
  }, [navigate]);

  // ListView reads actions straight out of whatever the mapper returns, so this
  // is where per-action behaviour gets attached without touching ListView.
  const searchResponseMapper = useCallback((response, mapperModule) => {
    const mapped = mapListResponse(response, mapperModule);
    // A mapper carrying a side effect is not ideal, but on the unified path
    // ListView owns the fetch and this is the only response hook it hands back.
    // It runs inside ListView's own .then(), so this is an ordinary
    // promise-callback update rather than a during-render setState.
    settleCollapseAfterSearch(
      Number(mapped.total) > 0 || (mapped.rows?.length ?? 0) > 0,
    );
    return { ...mapped, actions: (mapped.actions ?? []).map(withPostActionRoute) };
  }, [withPostActionRoute, settleCollapseAfterSearch]);

  const handleSearch = () => {
    // Each boolean-capable field carries its own blocking + warningOnly rule
    // (see the Form.Item below), so a malformed expression is rejected right
    // here by antd's own validation — no separate re-check needed.
    form.validateFields()
      .then((values) => {
        // Armed only once validation passed, so a blocked search never leaves
        // the form primed to fold away behind an error the user has to fix.
        collapseOnResultsRef.current = true;
        if (unifiedEndpoint) {
          // No fetch here — ListView does it. A fresh nonce remounts it, which
          // resets page and tab together for the new criteria.
          //
          // The sources are frozen here rather than read live, so changing the
          // Search Preference tick boxes without pressing Search cannot fire a
          // request on its own.
          setSubmittedSources(getSearchSources(values?.[SOURCE_FIELD]));
          setCriteria(buildCriteria(values));
          setSearchNonce((nonce) => nonce + 1);
          return;
        }
        runSearch(values, undefined, { current: 1 });
      })
      .catch(() => {
        // The message says "highlighted field", so the field has to be on
        // screen for it to mean anything.
        collapseOnResultsRef.current = false;
        setFormCollapsed(false);
        message.error('Fix the highlighted field before searching.');
      });
  };

  const handleReset = () => {
    // Clearing the form and leaving it folded away would be absurd — the whole
    // point of Reset is to start typing again.
    collapseOnResultsRef.current = false;
    setFormCollapsed(false);
    form.resetFields();
    setFormValues({});
    setFieldErrors({});
    setResults(EMPTY_RESULTS);
    setTabs([]);
    setSelectedSource('internal');
    setCriteria(null);
    setSubmittedSources(null);
  };

  const handleSourceAction = useCallback(async (sourceAction, record) => {
    const key = getSourceActionInstanceKey(sourceAction, record);
    setSourceActionState((current) => ({ ...current, [key]: { ...current[key], loading: true } }));
    try {
      await runSourceAction(sourceAction, record);
      setSourceActionState((current) => ({ ...current, [key]: { loading: false, completed: true } }));
      message.success(sourceAction.successMessage);
    } catch (error) {
      setSourceActionState((current) => ({ ...current, [key]: { loading: false } }));
      message.error(error?.message || sourceAction.errorMessage);
    }
  }, []);

  const resultFields = useMemo(() => {
    const base = results.fields?.length ? results.fields : (() => {
      const first = results.rows[0];
      if (!first) return [];
      return Object.keys(first)
        .filter((key) => !key.startsWith('_') && typeof first[key] !== 'object')
        .map((key) => ({
          value: key,
          field: key,
          label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
          isVisible: true,
        }));
    })();

    const sourceActions = getAvailableSourceActions({ rows: results.rows, selectedSource });
    if (!sourceActions.length) return base;

    const actionFields = new Set(sourceActions.map((item) => item.field));
    return [
      ...base.filter((field) => field.cardRole !== 'action' && !actionFields.has(field.value)),
      ...sourceActions.map((sourceAction) => ({
        value: sourceAction.field,
        field: sourceAction.field,
        label: sourceAction.label ?? 'Action',
        cardRole: 'action',
        isVisible: true,
        sorter: false,
        render: (_value, record) => {
          if (!sourceActionAppliesToRecord(sourceAction, record, selectedSource)) return null;
          const key = getSourceActionInstanceKey(sourceAction, record);
          const state = sourceActionState[key] ?? {};
          return (
            <Button
              type="primary"
              size="small"
              loading={state.loading}
              disabled={!key || state.completed}
              onClick={() => handleSourceAction(sourceAction, record)}
            >
              {state.completed ? sourceAction.completedLabel : sourceAction.buttonLabel}
            </Button>
          );
        },
      })),
    ];
  }, [handleSourceAction, results.fields, results.rows, selectedSource, sourceActionState]);

  // The record context this search runs in (e.g. the job being sourced for).
  // Shared by row actions and the configured link button's URL template, so
  // the two can never disagree about which record they mean.
  const actionContext = useMemo(
    () => searchActionContext ?? (migrationJobId ? { jobId: migrationJobId } : null),
    [searchActionContext, migrationJobId],
  );

  const resultActions = useMemo(() => {
    if (!actionContext) return results.actions;
    const merge = (item) => ({
      ...item,
      popupState: { ...actionContext, ...item.popupState },
      ...(item.children ? { children: item.children.map(merge) } : {}),
    });
    return (results.actions ?? []).map(merge);
  }, [results.actions, actionContext]);

  // Admin-configured link button (schema.actionButton*). Off by default, so a
  // form that never enables it renders exactly as it does today.
  // safeNavTarget resolves :tokens AND rejects anything that isn't a plain
  // in-app path — an admin-supplied URL is never interpolated raw.
  const actionButtonEnabled = Boolean(schema?.actionButtonEnabled);
  const actionButtonTarget = actionButtonEnabled
    ? safeNavTarget(schema?.actionButtonUrl, undefined, actionContext ?? {})
    : null;

  const handleActionButton = () => {
    if (!actionButtonTarget) return;
    if (schema?.actionButtonTarget === '_blank') {
      window.open(actionButtonTarget, '_blank', 'noopener');
      return;
    }
    navigate(actionButtonTarget);
  };

  // Boolean mode mirrors the backend's own EMPTY_QUERY rule: at least one
  // boolean-capable field must carry a value — filling only Location or
  // Experience is not a boolean search. Traditional mode keeps the existing
  // "any criterion at all" rule.
  const criteriaContext = { booleanMode: isBooleanMode };
  const hasBooleanCriteria = booleanFields.some((field) => fieldValueFilled(
    field, formValues[field.field], { ...criteriaContext, isPrimary: true },
  ));

  // A field the admin marked required must be filled before Search is offered,
  // whatever else the form holds. With req set on the Keywords field — which
  // the candidate search config does — this is exactly "Search enables only
  // once Keywords has something", reusing the `req` key every search form
  // already supports rather than hard-coding a keyword-specific rule.
  const missingRequired = visibleFields.filter((field) => truthy(field.req) && !fieldValueFilled(
    field, formValues[field.field], { ...criteriaContext, isPrimary: isPrimaryKeywordField(field) },
  ));

  const searchDisabled = missingRequired.length > 0
    || (isBooleanMode ? !hasBooleanCriteria : !hasCriteria(formValues, visibleFields, criteriaContext));

  const emptyBooleanHint = (() => {
    if (missingRequired.length) {
      return `Enter ${missingRequired.map((field) => field.label ?? field.field).join(', ')} to search.`;
    }
    if (isBooleanMode && !hasBooleanCriteria && booleanFields.length) {
      return `Enter a query in at least one of: ${booleanFields.map((field) => field.label ?? field.field).join(', ')}.`;
    }
    return '';
  })();

  if (!moduleName) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card"><Text type="danger">moduleName is required.</Text></Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card"><Skeleton active paragraph={{ rows: 4 }} /></Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card">
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <Text strong>Failed to load the search form</Text>
            <Text type="secondary">{loadError}</Text>
            <Button icon={<ReloadOutlined />} onClick={retry}>Retry</Button>
          </Space>
        </Card>
      </div>
    );
  }

  if (!fields.length) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card">
          <Text type="secondary">No search fields are configured for “{moduleName}”.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="sbv1-page">
      <Card
        // Collapsing hides the body with CSS rather than unmounting the Form.
        // Unmounting would re-run every remote option fetch in
        // SearchFieldControl on each expand, and KeywordsField's uncommitted
        // draft input lives in component state, so a half-typed keyword would
        // disappear. Hiding costs nothing and keeps both.
        className={`sbv1-card${formCollapsed ? ' sbv1-card-collapsed' : ''}`}
        title={<Text strong>{title ?? schema?.label ?? 'Search'}</Text>}
        extra={(
          <Space size={12}>
            {/* Every action lives up here in one row — nothing at the bottom of
                the field grid, so the controls never move as the form grows. */}
            <Button
              size="small"
              className="sbv1-action-btn"
              icon={<FilterOutlined />}
              onClick={handleReset}
            >
              Reset filters
            </Button>
            <Tooltip title={emptyBooleanHint || undefined}>
              <Button
                size="small"
                className="sbv1-action-btn"
                icon={<SearchOutlined />}
                disabled={searchDisabled}
                loading={results.loading}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Tooltip>
            {actionButtonEnabled && (
              // Disabled rather than hidden when the URL is missing or unsafe,
              // so a half-finished config is visible instead of silently dead.
              <Tooltip title={actionButtonTarget ? undefined : 'No valid destination configured'}>
                <Button
                  size="small"
                  className="sbv1-action-btn"
                  icon={<FileTextOutlined />}
                  disabled={!actionButtonTarget}
                  onClick={handleActionButton}
                >
                  {schema?.actionButtonLabel || 'Open'}
                </Button>
              </Tooltip>
            )}
            {onBack && (
              <Button size="small" className="sbv1-action-btn" onClick={onBack}>Back</Button>
            )}
            {/* Deliberately not "…search filters": that name contains "Search"
                and would be indistinguishable from the Search button to a
                screen reader querying by accessible name. */}
            <Tooltip title={formCollapsed ? 'Show filters' : 'Hide filters'}>
              <Button
                type="text"
                size="small"
                className="sbv1-collapse-btn"
                icon={formCollapsed ? <DownOutlined /> : <UpOutlined />}
                aria-label={formCollapsed ? 'Show filters' : 'Hide filters'}
                aria-expanded={!formCollapsed}
                aria-controls={formBodyId}
                onClick={() => setFormCollapsed((collapsed) => !collapsed)}
              />
            </Tooltip>
            {/* The Boolean toggle used to live here, switching whichever
                fields an admin had marked. It now sits inside the common
                Keywords block it actually governs — see KeywordsField. */}
          </Space>
        )}
      >
        <Form
          id={formBodyId}
          form={form}
          layout="vertical"
          colon={false}
          onValuesChange={(_changed, all) => setFormValues(all)}
        >
          <Row gutter={[24, 0]}>
            {visibleFields.map((field) => {
              const asBoolean = isBooleanMode && booleanFields.includes(field);
              // The keywords control renders its own expression box, so it is
              // never swapped for BooleanQueryField — but its expression still
              // gets the same validation rules.
              const asKeywords = isKeywordField(field);
              const expressionOf = (value) => (
                asKeywords ? normalizeKeywordValue(value).expression : value
              );
              const rules = [];
              if (truthy(field.req)) {
                rules.push({ required: true, message: `${field.label ?? field.field} is required` });
              }
              if (asBoolean) {
                // Blocking: a broken expression (unbalanced brackets, a
                // dangling operator, "&&" instead of "AND") stops the search —
                // the Go parser would reject it too, so this just saves the
                // round trip.
                rules.push({
                  validator: (_rule, value) => {
                    const { errors } = validateBooleanQuery(expressionOf(value));
                    return errors.length ? Promise.reject(new Error(errors[0])) : Promise.resolve();
                  },
                });
                // warningOnly: legal but likely-wrong (all-exclusions, a bare
                // "*"/"?" the backend treats as a literal character, …) shows
                // as an amber hint and never blocks form.validateFields().
                rules.push({
                  warningOnly: true,
                  validator: (_rule, value) => {
                    const { warnings } = validateBooleanQuery(expressionOf(value));
                    return warnings.length ? Promise.reject(new Error(warnings[0])) : Promise.resolve();
                  },
                });
              }
              return (
                <Col xs={24} sm={12} lg={field.col ?? 12} key={field.field}>
                  <Form.Item
                    name={field.field}
                    // A type that draws its own heading (the primary keywords
                    // block, which pairs its title with the Boolean toggle)
                    // would otherwise show the label twice.
                    label={ownsLabelFor(field) ? null : (field.label ?? field.field)}
                    rules={rules.length ? rules : undefined}
                    initialValue={defaultInitialValue(field)}
                  >
                    {asBoolean && !asKeywords
                      ? <BooleanQueryField field={field} serverError={fieldErrors[field.field]} />
                      : (
                        <SearchFieldControl
                          field={field}
                          moduleName={moduleName}
                          extraProps={asKeywords ? {
                            booleanEnabled,
                            booleanMode: isBooleanMode,
                            onBooleanModeChange: handleModeChange,
                            serverError: fieldErrors[field.field],
                          } : undefined}
                        />
                      )}
                  </Form.Item>
                </Col>
              );
            })}
          </Row>

          {/* The buttons moved to the card header; the hint stays down here
              next to the fields it is talking about. */}
          {emptyBooleanHint && (
            <div className="sbv1-actions">
              <Text type="secondary" className="sbv1-empty-hint">{emptyBooleanHint}</Text>
            </div>
          )}
        </Form>
      </Card>

      {/* Unified path: ListView owns fetching, tabs, paging, sort and
          selection. Deliberately no onPageChange/onPageSizeChange/onSearch —
          those set isControlled and switch its own fetching off. */}
      {unifiedEndpoint && searchFetcher && (
        <ListView
          key={searchNonce}
          moduleName={moduleName}
          breadcrumbItems={[]}
          fetchData={searchFetcher}
          responseMapper={searchResponseMapper}
          queryParams={criteria}
          rowKey={resolveRowKey}
          showSearch={false}
          activeView={activeView}
          onViewChange={setActiveView}
          showViewToggle
          // This screen owns its view. Without this, ListView overrides
          // activeView and forces cards for any row whose sourceType is not
          // "internal" — which is every Dice row — so the Dice tab could never
          // open as a list and the toggle did nothing on it.
          autoCardForExternalRows={false}
        />
      )}

      {!unifiedEndpoint && results.searched && (
        <ListView
          moduleName={moduleName}
          breadcrumbItems={[]}
          dataSource={results.rows}
          fields={resultFields}
          total={results.total}
          loading={results.loading}
          current={pagination.current}
          pageSize={pagination.pageSize}
          showSearch={false}
          tabs={tabs}
          selectedSource={selectedSource}
          onTabChange={(key) => runSearch(lastValues ?? {}, key, { current: 1 })}
          onPageChange={(current, pageSize) => runSearch(lastValues ?? {}, selectedSource, { current, pageSize })}
          onPageSizeChange={(current, pageSize) => runSearch(lastValues ?? {}, selectedSource, { current: 1, pageSize })}
          actions={resultActions}
          actionRules={results.actionRules}
        />
      )}
    </div>
  );
}

SearchBuilderV1.propTypes = {
  moduleName: PropTypes.string.isRequired,
  action: PropTypes.string,
  formType: PropTypes.string,
  title: PropTypes.string,
  migrationJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchActionContext: PropTypes.object,
  // { [sourceKey]: moduleKey }, overridden by any option that configures its
  // own `module` — see buildSourceModuleMap.
  sourceModules: PropTypes.object,
  onBack: PropTypes.func,
};
